import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';
import { useNavigate } from 'react-router-dom';
import BusinessCard, { CARD_STYLES } from '../../components/commercial/BusinessCard';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import confetti from 'canvas-confetti';
import CommercialLayout from '../../components/commercial/CommercialLayout';

export default function CommercialProfilePage() {
    const { user } = useAuth();
    const navigate = useNavigate();

    // -- STATE --
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profileId, setProfileId] = useState(null);

    // Fields
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        city: '',
        bio: '',
        specialty: '', // Chasseur, Eleveur
    });
    const [photo, setPhoto] = useState(null); // File object
    const [photoPreview, setPhotoPreview] = useState(null); // URL for preview

    // XP & Level
    const [xp, setXp] = useState(0);
    const [isActivated, setIsActivated] = useState(false);

    // 3D Rotation State
    const [rotation, setRotation] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const lastPos = useRef({ x: 0, y: 0 });
    const [dragActive, setDragActive] = useState(false);

    // Drag Logic
    const handleMouseDown = (e) => {
        setIsDragging(true);
        lastPos.current = { x: e.clientX, y: e.clientY };
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            const deltaX = e.clientX - lastPos.current.x;
            const deltaY = e.clientY - lastPos.current.y;

            setRotation(prev => ({
                x: prev.x - deltaY * 0.5, // Tilt Up/Down
                y: prev.y + deltaX * 0.5  // Spin Left/Right
            }));
            lastPos.current = { x: e.clientX, y: e.clientY };
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
    }, [isDragging]);

    // Effects
    useEffect(() => {
        loadProfile();
    }, []);

    useEffect(() => {
        calculateXP();
    }, [formData, photoPreview]);

    // -- LOAD DATA --
    const loadProfile = async () => {
        try {
            setLoading(true);
            const record = await pb.collection('commercial_profiles').getFirstListItem(`user="${user.id}"`);
            if (record) {
                setProfileId(record.id);
                setFormData({
                    firstname: record.firstname || '',
                    lastname: record.lastname || '',
                    email: record.email || '',
                    phone: record.phone || '',
                    city: record.city || '',
                    bio: record.bio || '',
                    specialty: record.specialty || '',
                    card_style: record.card_style || 'obsidian',
                });
                if (record.photo) {
                    setPhotoPreview(pb.files.getUrl(record, record.photo));
                }
                if (record.level >= 1) {
                    setIsActivated(true);
                }
            }
        } catch (err) {
            // No profile yet, that's fine
            console.log("No profile found, creating new one...");
        } finally {
            setLoading(false);
        }
    };

    // -- CALCULATE XP --
    const calculateXP = () => {
        let score = 0;
        if (formData.lastname) score += 10;
        if (formData.firstname) score += 10;
        if (formData.email) score += 10;
        if (formData.phone) score += 10;
        if (formData.city) score += 10;
        if (formData.bio && formData.bio.length > 10) score += 20;
        if (formData.specialty) score += 10;
        if (photoPreview) score += 20;

        setXp(Math.min(score, 100));
    };

    // -- HANDLERS --
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePhotoFile = (file) => {
        if (file) {
            setPhoto(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handlePhotoChange = (e) => {
        handlePhotoFile(e.target.files[0]);
    };

    // Drag Handlers
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handlePhotoFile(e.dataTransfer.files[0]);
        }
    };

    const handleActivate = async () => {
        if (xp < 100) return;
        setSaving(true);
        try {
            const data = {
                ...formData,
                user: user.id,
                xp: 100,
                level: 1, // ACTIVATED
            };

            let record;
            if (profileId) {
                if (photo) data.photo = photo;
                record = await pb.collection('commercial_profiles').update(profileId, data);
            } else {
                data.photo = photo;
                record = await pb.collection('commercial_profiles').create(data);
                setProfileId(record.id);
            }

            // SUCCESS ANIMATION
            setIsActivated(true);
            triggerConfetti();

            // Wait for visual effect then redirect
            setTimeout(() => {
                navigate('/commercial/formation');
            }, 3000);

        } catch (err) {
            console.error(err);
            alert("Erreur lors de l'activation : " + err.message);
        } finally {
            setSaving(false);
        }
    };

    // Save draft without activating
    const handleSaveDraft = async () => {
        setSaving(true);
        try {
            // Logic similar to activate but keep level 0 if not 100%
            // For now simple save
            const data = { ...formData, user: user.id, xp: xp };
            if (isActivated) data.level = 1;

            if (profileId) {
                if (photo) data.photo = photo;
                await pb.collection('commercial_profiles').update(profileId, data);
            } else {
                data.photo = photo;
                const record = await pb.collection('commercial_profiles').create(data);
                setProfileId(record.id);
            }
        } catch (err) { console.error(err); }
        setSaving(false);
    };

    const triggerConfetti = () => {
        confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#ef4444', '#22c55e', '#3b82f6'] // Red, Green, Blue
        });
    };

    // -- QR CODE URL --
    // We use a safe public API for now. In real prod, use a dedicated library.
    // Link format: infocolab.fr/?ref=[ID]
    const referralLink = `https://infocolab.com/?ref=${user.id}`;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(referralLink)}&color=000000&bgcolor=ffffff`;


    // -- SHARE & DOWNLOAD --
    const printRef = useRef(null);

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `Carte de Visite - ${formData.firstname} ${formData.lastname}`,
                    text: `Voici ma carte de visite numérique InfoColab.`,
                    url: referralLink,
                });
            } catch (error) {
                console.log('Partage annulé ou échoué:', error);
            }
        } else {
            navigator.clipboard.writeText(referralLink)
                .then(() => alert("🔗 Lien copié dans le presse-papier !"))
                .catch(() => alert("Erreur lors de la copie du lien."));
        }
    };

    const handleDownload = async () => {
        if (!printRef.current) return;
        const frontEl = printRef.current.querySelector('#print-front');
        const backEl = printRef.current.querySelector('#print-back');

        if (!frontEl || !backEl) return;

        try {
            const scale = 4; // High Res
            const canvasFront = await html2canvas(frontEl, { scale, useCORS: true, backgroundColor: null });
            const canvasBack = await html2canvas(backEl, { scale, useCORS: true, backgroundColor: null });

            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: [85, 55]
            });

            pdf.addImage(canvasFront.toDataURL('image/png'), 'PNG', 0, 0, 85, 55);
            pdf.addPage();
            pdf.addImage(canvasBack.toDataURL('image/png'), 'PNG', 0, 0, 85, 55);

            pdf.save(`Carte_Visite_${formData.lastname || 'Agent'}.pdf`);
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la génération du PDF.");
        }
    };


    if (loading) return <div style={{ height: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: 'white' }}>Chargement du Dossier...</div>;

    return (
        <CommercialLayout title="Dossier Agent">
            <div className="commercial-profile-page" style={{
                width: '100%', minHeight: '100%', background: 'var(--commercial-bg)', color: 'var(--commercial-text)', fontFamily: 'Inter, sans-serif',
                display: 'flex', flexDirection: 'column', overflowX: 'hidden', borderRadius: 12
            }}>

                {/* --- HEADER --- */}
                <div style={{ padding: '20px 40px', background: 'var(--commercial-panel)', borderBottom: '1px solid var(--commercial-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                        <div style={{ fontSize: '1.5rem' }}>🕵️‍♂️</div>
                        <div>
                            <h1 style={{ fontSize: '1.2rem', fontWeight: 700, margin: 0, letterSpacing: '1px' }}>CONFIGURATION DE MON PROFIL</h1>
                            <div style={{ fontSize: '0.8rem', opacity: 0.5, textTransform: 'uppercase' }}>Carte de visite numérique</div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>STATUT ACCRÉDITATION</div>
                            <div style={{ fontWeight: 700, color: isActivated ? '#22c55e' : '#ef4444' }}>
                                {isActivated ? 'CERTIFIÉ ✅' : 'EN ATTENTE'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- CONTENT --- */}
                <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', padding: 40, gap: 60, justifyContent: 'center', alignItems: 'flex-start' }}>

                    {/* --- LEFT: 3D CARD WORKSTATION --- */}
                    <div style={{ flex: '0 0 400px', display: 'flex', flexDirection: 'column', alignItems: 'center', perspective: '1000px' }}>

                        <h2 style={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '2px', opacity: 0.5, marginBottom: 20 }}>Aperçu Temps Réel</h2>

                        {/* BUSINESS CARD COMPONENT */}
                        <BusinessCard
                            formData={formData}
                            photoPreview={photoPreview}
                            isActivated={isActivated}
                            rotation={rotation}
                            isDragging={isDragging}
                            onMouseDown={handleMouseDown}
                            qrCodeUrl={qrCodeUrl}
                            mode="3d"
                        />

                        <div style={{ marginTop: 20, opacity: 0.5, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🖐️</span> Maintenez et glissez pour tourner
                            <button type="button" onClick={() => setRotation({ x: 0, y: 0 })} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, color: 'white', padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer', marginLeft: 10 }}>Reset</button>
                        </div>

                        {/* ACTIONS */}
                        <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
                            <button
                                onClick={() => {
                                    handleShare();
                                    const btn = document.getElementById('share-btn-text');
                                    if (btn) {
                                        const original = btn.innerText;
                                        btn.innerText = "Copié ! ✅";
                                        setTimeout(() => btn.innerText = original, 2000);
                                    }
                                }}
                                style={{
                                    flex: 1, padding: '10px 15px', background: 'var(--commercial-panel)', border: '1px solid var(--commercial-border)', borderRadius: 8,
                                    color: 'var(--commercial-text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center',
                                    transition: 'all 0.2s', fontWeight: 600
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--commercial-panel-2)'; e.currentTarget.style.borderColor = 'var(--commercial-accent)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = 'var(--commercial-panel)'; e.currentTarget.style.borderColor = 'var(--commercial-border)'; }}
                            >
                                <span id="share-btn-text">🔗 Partager le lien</span>
                            </button>
                            <button
                                onClick={handleDownload}
                                style={{
                                    flex: 1, padding: '10px 15px', background: '#3b82f6', border: 'none', borderRadius: 8,
                                    color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center',
                                    fontWeight: 600, transition: 'transform 0.1s'
                                }}
                                onActive={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
                            >
                                🖨️ Télécharger PDF
                            </button>
                        </div>

                        {/* HIDDEN PRINT AREA */}
                        <div ref={printRef} style={{ position: 'absolute', top: -9999, left: -9999 }}>
                            <BusinessCard id="print-front" formData={formData} photoPreview={photoPreview} isActivated={isActivated} qrCodeUrl={qrCodeUrl} mode="front" />
                            <BusinessCard id="print-back" formData={formData} photoPreview={photoPreview} isActivated={isActivated} qrCodeUrl={qrCodeUrl} mode="back" />
                        </div>

                        <div style={{ marginTop: 20, opacity: 0.5, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🖐️</span> Maintenez et glissez pour tourner la carte
                            <button type="button" onClick={() => setRotation({ x: 0, y: 0 })} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, color: 'white', padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer', marginLeft: 10 }}>Reset</button>
                        </div>

                        {isActivated && (
                            <div className="animate-stamp-in" style={{
                                marginTop: 40, border: '4px solid #22c55e', color: '#22c55e', // Green color
                                padding: '10px 30px', borderRadius: 8, fontSize: '2rem', fontWeight: 900,
                                transform: 'rotate(-20deg)', opacity: 0.9, letterSpacing: '4px'
                            }}>
                                CERTIFIÉ
                            </div>
                        )}
                    </div>


                    {/* --- RIGHT: FORM TERMINAL --- */}
                    <div style={{ flex: 1, minWidth: 350, maxWidth: 600, background: 'var(--commercial-panel)', backdropFilter: 'blur(10px)', borderRadius: 24, border: '1px solid var(--commercial-border)', padding: 40 }}>

                        <h2 style={{ fontSize: '1.8rem', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span>👤</span> Mon Profil
                        </h2>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                            <div className="form-group">
                                <label style={labelStyle}>Nom</label>
                                <input name="lastname" value={formData.lastname} onChange={handleChange} placeholder="ex: BOND" style={inputStyle} />
                            </div>
                            <div className="form-group">
                                <label style={labelStyle}>Prénom</label>
                                <input name="firstname" value={formData.firstname} onChange={handleChange} placeholder="ex: James" style={inputStyle} />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                            <div className="form-group">
                                <label style={labelStyle}>Email Pro</label>
                                <input name="email" value={formData.email} onChange={handleChange} placeholder="james@infocolab.fr" style={inputStyle} />
                            </div>
                            <div className="form-group">
                                <label style={labelStyle}>Téléphone</label>
                                <input
                                    type="text"
                                    inputMode="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="06..."
                                    style={inputStyle}
                                />
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>Secteur / Ville</label>
                            <input name="city" value={formData.city} onChange={handleChange} placeholder="ex: Paris & IDF" style={inputStyle} />
                        </div>

                        <div className="form-group" style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>Pitch (Phrase d'accroche)</label>
                            <textarea name="bio" value={formData.bio} onChange={handleChange} placeholder="Expert en transformation digitale..." style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                            <div className="form-group">
                                <label style={labelStyle}>Spécialité</label>
                                <select name="specialty" value={formData.specialty} onChange={handleChange} style={inputStyle}>
                                    <option value="">Choisir...</option>
                                    <option value="Expert Digital">Expert Digital</option>
                                    <option value="Chargé d'Affaires">Chargé d'Affaires</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label style={labelStyle}>Design de la Carte</label>
                                <select name="card_style" value={formData.card_style || 'obsidian'} onChange={handleChange} style={inputStyle}>
                                    <option value="">Choisir...</option>
                                    {Object.entries(CARD_STYLES).map(([key, style]) => (
                                        <option key={key} value={key}>{style.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 20 }}>
                            <div className="form-group">
                                <label style={labelStyle}>Photo Professionnelle</label>
                                <div
                                    onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                                    style={{
                                        border: '2px dashed ' + (dragActive ? 'var(--commercial-accent)' : 'var(--commercial-border)'),
                                        borderRadius: 12, padding: 30, textAlign: 'center',
                                        background: dragActive ? 'rgba(79, 70, 229, 0.1)' : 'var(--commercial-panel-2)',
                                        color: 'var(--commercial-text)',
                                        transition: 'all 0.2s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => document.getElementById('photo-upload').click()}
                                >
                                    {photoPreview ? (
                                        <div style={{ position: 'relative', width: 80, height: 80, borderRadius: 8, overflow: 'hidden', marginBottom: 10, boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
                                            <img src={photoPreview} alt="Miniature" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPhoto(null);
                                                    setPhotoPreview(null);
                                                }}
                                                style={{
                                                    position: 'absolute', top: 2, right: 2,
                                                    background: 'rgba(239, 68, 68, 0.9)', color: 'white',
                                                    border: 'none', borderRadius: '50%', width: 20, height: 20,
                                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '12px', fontWeight: 'bold'
                                                }}
                                                title="Supprimer"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: '2rem' }}>☁️</div>
                                    )}
                                    <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                                        {photo ? <span style={{ color: '#22c55e' }}>Photo sélectionnée : {photo.name}</span> : "Glisser une photo ici ou cliquer"}
                                    </div>
                                    <input
                                        id="photo-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePhotoChange}
                                        style={{ display: 'none' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: 30 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.9rem' }}>
                                <span style={{ opacity: 0.7 }}>Indice de Complétion</span>
                                <span style={{ fontWeight: 700, color: '#3b82f6' }}>{xp}%</span>
                            </div>
                            <div style={{ height: 10, background: 'rgba(255,255,255,0.1)', borderRadius: 10, overflow: 'hidden' }}>
                                <div style={{
                                    height: '100%', width: `${xp}%`,
                                    background: xp === 100 ? '#22c55e' : '#3b82f6',
                                    transition: 'width 0.5s ease-out, background 0.5s'
                                }} />
                            </div>
                            <div style={{ fontSize: '0.8rem', opacity: 0.5, marginTop: 6, fontStyle: 'italic' }}>
                                {xp < 100 ? "Complétez tous les champs pour activer l'agent." : "Prêt pour activation."}
                            </div>
                        </div>

                        <button
                            onClick={handleActivate}
                            disabled={xp < 100 || saving}
                            style={{
                                width: '100%', padding: 24, borderRadius: 12, border: 'none',
                                background: isActivated ? '#3b82f6' : (xp === 100 ? '#f59e0b' : 'rgba(255,255,255,0.1)'), // Blue or Gold
                                color: isActivated ? 'white' : (xp === 100 ? 'black' : 'rgba(255,255,255,0.3)'),
                                fontSize: '1.2rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px',
                                cursor: (xp === 100 && !saving) ? 'pointer' : 'not-allowed',
                                transition: 'all 0.3s',
                                boxShadow: (xp === 100 && !isActivated) ? '0 10px 30px rgba(245, 158, 11, 0.4)' : 'none',
                                transform: (xp === 100 && !isActivated) ? 'scale(1.02)' : 'scale(1)'
                            }}
                        >
                            {saving ? 'Traitement...' : isActivated ? 'Mettre à jour le dossier' : 'Activer ma Carte Pro'}
                        </button>

                    </div>
                </div>

                <style>{`
                .commercial-profile-page input::placeholder, .commercial-profile-page textarea::placeholder { color: var(--commercial-muted); opacity: 0.7; }
                @keyframes stampIn {
                    0% { transform: scale(3) rotate(0deg); opacity: 0; }
                    80% { transform: scale(1) rotate(-20deg); opacity: 1; }
                    100% { transform: scale(1) rotate(-20deg); opacity: 0.9; }
                }
                .animate-stamp-in { animation: stampIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
            `}</style>
            </div>
        </CommercialLayout>
    );
}

const labelStyle = { display: 'block', marginBottom: 4, fontSize: '0.85rem', color: 'var(--commercial-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' };
const inputStyle = { width: '100%', padding: '16px', background: 'var(--commercial-panel-2)', border: '1px solid var(--commercial-border)', borderRadius: 8, color: 'var(--commercial-text)', fontSize: '1rem', outline: 'none', transition: 'border 0.2s' };
